import React from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Heart, Leaf, ArrowRight, Play, Calendar, Clock, BookOpen, Award, Users } from 'lucide-react';
import { mockData } from '../mock';
import SEOHead from '../components/SEOHead';
import SocialShare from '../components/SocialShare';

const Home = () => {
  const iconMap = {
    TrendingUp: TrendingUp,
    Heart: Heart,
    Leaf: Leaf
  };

  return (
    <div className="min-h-screen">
      <SEOHead 
        title="Home"
        description="Dr. Lim Siow Jin - Visionary Entrepreneur, Zen Master & Wellness Pioneer. Founder of DXN Holdings (180+ countries), Sunya meditation teacher, and global peace advocate."
        keywords="Dr. Lim Siow Jin, DXN founder, Sunya meditation, Ganoderma, wellness, spirituality, Traditional Chinese Medicine"
      />
      
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src={mockData.hero.image}
            alt="Dr. Lim Siow Jin"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900/90 via-emerald-900/80 to-slate-800/90"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              {mockData.hero.name}
            </h1>
            <p className="text-2xl md:text-3xl text-emerald-300 font-light">
              {mockData.hero.title}
            </p>
            <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
              {mockData.hero.subtitle}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-8">
              {mockData.hero.cta.map((cta, index) => (
                <Link
                  key={index}
                  to={cta.link}
                  className={`px-8 py-4 rounded-full font-semibold transition-all transform hover:scale-105 flex items-center space-x-2 ${
                    index === 0
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg hover:shadow-xl'
                      : 'bg-white/10 backdrop-blur-md hover:bg-white/20 text-white border border-white/30'
                  }`}
                >
                  <span>{cta.text}</span>
                  <ArrowRight size={20} />
                </Link>
              ))}
            </div>
            
            {/* Social Share on Hero */}
            <div className="flex justify-center pt-4">
              <SocialShare 
                title="Dr. Lim Siow Jin - Visionary Entrepreneur | Zen Master | Wellness Pioneer"
                description="Discover the integrated journey of business excellence, natural wellness, and spiritual wisdom"
              />
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6 mt-20">
            {mockData.stats.map((stat, index) => (
              <div
                key={index}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20 hover:bg-white/20 transition-all"
              >
                <div className="text-3xl md:text-4xl font-bold text-emerald-300">{stat.number}</div>
                <div className="text-sm text-gray-300 mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10 animate-bounce">
          <div className="w-6 h-10 rounded-full border-2 border-white/50 flex items-start justify-center p-2">
            <div className="w-1 h-2 bg-white/70 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Three Pillars Section */}
      <section className="py-24 bg-gradient-to-br from-gray-50 to-emerald-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              One Life, Three Pillars
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Integrating business excellence, natural wellness, and spiritual wisdom for holistic transformation
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {mockData.pillars.map((pillar) => {
              const IconComponent = iconMap[pillar.icon];
              return (
                <div
                  key={pillar.id}
                  className="group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={pillar.image}
                      alt={pillar.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center space-x-3 mb-2">
                        <div className="w-12 h-12 rounded-full bg-emerald-600 flex items-center justify-center">
                          <IconComponent className="text-white" size={24} />
                        </div>
                        <div>
                          <h3 className="text-2xl font-bold text-white">{pillar.title}</h3>
                          <p className="text-emerald-300 text-sm">{pillar.subtitle}</p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-6 space-y-4">
                    <p className="text-gray-600 leading-relaxed">{pillar.description}</p>
                    
                    {/* Highlights */}
                    <ul className="space-y-2">
                      {pillar.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-center space-x-2 text-sm text-gray-700">
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-600"></div>
                          <span>{highlight}</span>
                        </li>
                      ))}
                    </ul>

                    <Link
                      to={pillar.link}
                      className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold group/link mt-4"
                    >
                      <span>Learn More</span>
                      <ArrowRight size={18} className="group-hover/link:translate-x-1 transition-transform" />
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Latest Talks Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">Latest Talks</h2>
              <p className="text-gray-600">Wisdom shared through educational content</p>
            </div>
            <Link
              to="/talks"
              className="hidden md:inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <span>View All Talks</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {mockData.latestTalks.map((talk) => (
              <div
                key={talk.id}
                className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={talk.thumbnail}
                    alt={talk.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="text-emerald-700" size={28} fill="currentColor" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 right-2 bg-black/70 backdrop-blur-sm px-2 py-1 rounded text-white text-xs flex items-center space-x-1">
                    <Clock size={12} />
                    <span>{talk.duration}</span>
                  </div>
                </div>
                <div className="p-4">
                  <div className="text-xs font-semibold text-emerald-700 mb-2">{talk.category}</div>
                  <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2 group-hover:text-emerald-700 transition-colors">
                    {talk.title}
                  </h3>
                  <p className="text-sm text-gray-600 line-clamp-2">{talk.description}</p>
                  <div className="flex items-center space-x-2 mt-3 text-xs text-gray-500">
                    <Calendar size={12} />
                    <span>{new Date(talk.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8 md:hidden">
            <Link
              to="/talks"
              className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <span>View All Talks</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Latest Articles Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-50 to-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-2">Latest Articles</h2>
              <p className="text-gray-600">Insights and teachings in written form</p>
            </div>
            <Link
              to="/articles"
              className="hidden md:inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <span>View All Articles</span>
              <ArrowRight size={18} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockData.articles.map((article) => (
              <article
                key={article.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 group"
              >
                <div className="relative h-56 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-emerald-600 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      {article.category}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mb-3">
                    <div className="flex items-center space-x-1">
                      <Calendar size={12} />
                      <span>{new Date(article.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                    </div>
                    <div className="flex items-center space-x-1">
                      <BookOpen size={12} />
                      <span>{article.readTime}</span>
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-emerald-700 transition-colors">
                    {article.title}
                  </h3>
                  <p className="text-gray-600 mb-4 line-clamp-3">{article.excerpt}</p>
                  <Link
                    to={`/articles/${article.id}`}
                    className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
                  >
                    <span>Read More</span>
                    <ArrowRight size={16} />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          <div className="text-center mt-8 md:hidden">
            <Link
              to="/articles"
              className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <span>View All Articles</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Awards & Recognition Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center">
                <Award className="text-white" size={32} />
              </div>
            </div>
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Awards & Recognition</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Honored globally for contributions to business, wellness, and humanitarian service
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {mockData.awards.map((award, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border border-amber-200 hover:shadow-lg transition-all"
              >
                <div className="flex items-start space-x-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 mt-1">
                    <Award className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">{award.title}</h3>
                    <p className="text-sm text-gray-600 mb-1">{award.issuer}</p>
                    <p className="text-xs text-amber-700 font-semibold">{award.year}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/about/awards"
              className="inline-flex items-center space-x-2 text-emerald-700 hover:text-emerald-800 font-semibold"
            >
              <span>View All Awards</span>
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-gradient-to-br from-emerald-700 via-emerald-600 to-teal-600 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img
            src="https://images.unsplash.com/photo-1567372704182-ec2ea042c937"
            alt="Background"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Begin Your Journey to Peace & Prosperity
          </h2>
          <p className="text-xl text-emerald-100 mb-8 max-w-2xl mx-auto">
            Discover Sunya meditation, explore ancient wisdom, and join a global community committed to transformation
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sunya/retreats"
              className="px-8 py-4 bg-white text-emerald-700 rounded-full font-semibold hover:bg-gray-100 transition-all transform hover:scale-105 inline-flex items-center justify-center space-x-2"
            >
              <span>Join a Retreat</span>
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/sunya/online"
              className="px-8 py-4 bg-emerald-800 text-white rounded-full font-semibold hover:bg-emerald-900 transition-all transform hover:scale-105 inline-flex items-center justify-center space-x-2 border border-white/30"
            >
              <span>Start Online Training</span>
              <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;